import { useApiQuery } from "@gladis/api-client";
import { formatEur, type PropertyDetailDto } from "@gladis/shared";
import { EmptyState, ErrorState, LoadingState, Table } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import type { ReactNode } from "react";

import { valuationSourceLabel } from "../estimate-display.js";

/** One property's detail, including its full valuation history. */
export const propertyDetailKey = (propertyId: string) =>
  ["properties", "detail", propertyId] as const;

export function usePropertyDetail(
  propertyId: string,
): ReturnType<typeof useApiQuery<PropertyDetailDto>> {
  return useApiQuery<PropertyDetailDto>(propertyDetailKey(propertyId), `/properties/${propertyId}`);
}

/**
 * How a property's worth has been stated over time — newest first (FR-016).
 *
 * THERE IS NO EDIT CONTROL AND NO DELETE CONTROL HERE, and their absence is the feature rather than
 * an omission from it. A correction is a newer entry, and the older one stays on this list: the
 * history records what the household believed and when, instead of a single number whose changes
 * nobody kept. The absence is enforced five ways — no control here, no route, no repository method,
 * no policy, no privilege — and an architecture test asserts the application never even tries.
 *
 * THE SOURCE COLUMN NOW DISTINGUISHES THREE THINGS, and that is 017's whole contribution here.
 *
 * "By hand" and "WOZ" are numbers a member stated. "Estimated" is the household's own last real
 * number carried forward by its region's price index — it can see no renovation, no extension, no
 * new energy label, and it drifts further from reality the longer the anchor goes unrefreshed.
 *
 * A reader scanning this list must be able to tell which is which at a glance, because the failure
 * mode of an indexed value is not a wrong number: it is a number that looks maintained. The column
 * was here from the start, reading "By hand" for every row, precisely so that this arrival needed no
 * reshaping.
 */
export function ValuationHistory({ propertyId }: { propertyId: string }): ReactNode {
  const t = useT();
  const detail = usePropertyDetail(propertyId);

  /*
   * A 404 IS NOT AN ERROR, IT IS AN ANSWER (018's FR-016).
   *
   * Until the activity feed existed, this page was only ever reached by clicking a row in a list, so the
   * record was there by construction. The feed can now link to a record that has since been removed, and it
   * deliberately does not check first — that would be a query per row on every page. So the destination
   * reports it, and "could not load" is the wrong sentence for a record that is simply gone.
   */
  if (detail.isPending) return <LoadingState>{t("prop.valuation.loading")}</LoadingState>;
  if (detail.error?.status === 404) return <EmptyState>{t("prop.valuation.gone")}</EmptyState>;
  if (detail.isError) return <ErrorState>{t("prop.valuation.loadFailed")}</ErrorState>;

  const valuations = detail.data?.valuations ?? [];
  if (valuations.length === 0) {
    // Unreachable for a property this application created — FR-008 guarantees an opening valuation.
    // Rendered rather than asserted, because an empty table with no explanation reads as a bug.
    return <EmptyState>{t("prop.valuation.empty")}</EmptyState>;
  }

  return (
    /*
     * NAMED, because the page has two tables and an E2E assertion used to reach this one by
     * position — `getByRole("table").last()`. Moving the valuation history above the mortgage
     * section silently pointed that at the wrong table. A name is what makes the reference say
     * which table it means; the same lesson `PropertiesChart` records for its heading.
     */
    <Table aria-label={t("prop.valuation.heading")}>
      <Table.Head>
        <Table.Row>
          <Table.HeaderCell>{t("prop.form.asOf")}</Table.HeaderCell>
          <Table.HeaderCell numeric>{t("prop.list.colValue")}</Table.HeaderCell>
          <Table.HeaderCell>{t("prop.valuation.colSource")}</Table.HeaderCell>
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {valuations.map((valuation) => (
          <Table.Row key={valuation.id}>
            <Table.Cell>{valuation.valuationDate}</Table.Cell>
            <Table.Cell numeric>{formatEur(valuation.value)}</Table.Cell>
            <Table.Cell>{valuationSourceLabel(valuation.source)}</Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
}
